import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import type { FiltersProps, IFiltersDef, TFilterKeys } from '../types';
import FiltersDef, { getFiltersLabels } from './Filters/FiltersDef';

const DataTableFilters = ({
  filtersDef,
  onChangeFilter,
  onSelectedFiltersChange,
  filtersOptions,
  favoriteFilters = [],
  limitOfFilters = 3,
  filtersLoading = false,
  filtersOnSecondRow = false,
}: FiltersProps) => {
  const { t } = useTranslation('common');
  const filterLabels = getFiltersLabels(t);

  invariant(filtersDef, 'data is not defined on DataTableFilters');
  invariant(onChangeFilter, 'onChangeFilter is not defined on DataTableFilters');

  // get filters from data
  const allFilters = FiltersDef.filter((filter) => filtersDef?.some((f) => f.key === filter.key)).sort((a, b) => {
    const indexA = filtersDef.findIndex((f) => f.key === a.key);
    const indexB = filtersDef.findIndex((f) => f.key === b.key);
    return indexA - indexB;
  });

  const fallbackFilters: TFilterKeys[] = allFilters.slice(0, limitOfFilters).map((item) => item.key) || [];

  const activeFilters = filtersDef.filter((filter) => (Array.isArray(filter.value) ? filter.value.length > 0 : filter.value !== undefined));
  const startersFilters = favoriteFilters.length > 0 ? activeFilters.map((filter) => filter.key).concat(favoriteFilters) : fallbackFilters;

  // state to show filters (hooks must run unconditionally)
  const [showFilters, setShowFilters] = useState<TFilterKeys[]>(startersFilters);

  // get filters to render
  const renderFilters = allFilters?.filter((filter) => showFilters.includes(filter.key));

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const [scrollShadows, setScrollShadows] = useState({ left: false, right: false });
  const prevShadowsRef = useRef(scrollShadows);

  const updateScrollShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflow = scrollWidth > clientWidth;
    const next = {
      left: overflow && scrollLeft > 2,
      right: overflow && scrollLeft + clientWidth < scrollWidth - 2,
    };
    if (prevShadowsRef.current.left !== next.left || prevShadowsRef.current.right !== next.right) {
      prevShadowsRef.current = next;
      setScrollShadows(next);
    }
  }, []);

  const scrollBy = useCallback((delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: showFilters re-runs effect so shadows update after filter list changes
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollShadows);
    ro.observe(el);
    // also observe the inner content so scrollWidth changes from filter resizes are detected
    if (scrollContentRef.current) ro.observe(scrollContentRef.current);
    el.addEventListener('scroll', updateScrollShadows);
    updateScrollShadows();
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateScrollShadows);
    };
  }, [updateScrollShadows, showFilters]);

  // get filters to render on dropdown
  const renderFiltersDropdown = allFilters?.filter((filter) => {
    const isVisible = showFilters.includes(filter.key);
    return !isVisible;
  }).length;

  const totalFilters = filtersDef?.length;
  const totalFiltersDropdown = totalFilters - renderFiltersDropdown;
  const labelFilterLength = totalFiltersDropdown === 0 ? t('buttons.filters') : `(${totalFiltersDropdown}/${totalFilters}) ${t('buttons.filters')}`;

  const onClick = useCallback(
    (filter: IFiltersDef) => {
      const newFilters = showFilters.includes(filter.key) ? showFilters.filter((key) => key !== filter.key) : showFilters.concat(filter.key);

      setShowFilters(newFilters);
      onSelectedFiltersChange?.(newFilters);
    },
    [showFilters, onSelectedFiltersChange]
  );

  if (filtersLoading) {
    return <Skeleton width={120} height={32} />;
  }

  return (
    <div className='no-scrollbar flex items-center gap-2 overflow-hidden' data-testid='datatable-filters'>
      {filtersOnSecondRow && filtersDef?.length < limitOfFilters && <Label color='secondary'>{t('buttons.filters')}:</Label>}
      {totalFilters > limitOfFilters && (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button variant='outlined' size='sm' color='primary' startIcon={<Icon name='list-filter' />} data-testid='datatable-filters-button' className='mr-2 w-36 shrink-0'>
              {labelFilterLength}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align='start'>
            {allFilters.map((filter) => (
              <DropdownMenu.CheckboxItem key={filter.key} checked={showFilters.includes(filter.key)} onCheckedChange={() => onClick(filter)}>
                <DropdownMenu.Label>{filterLabels[filter.key]}</DropdownMenu.Label>
              </DropdownMenu.CheckboxItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
      <AnimatePresence initial={false}>
        {(scrollShadows.left || scrollShadows.right) && (
          <motion.div
            key='arrow-left'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className='shrink-0'
          >
            <Button
              variant='ghost'
              size='sm'
              color='default'
              icon={<Icon name='chevron-left' />}
              onClick={() => scrollBy(-150)}
              disabled={!scrollShadows.left}
              aria-label={t('buttons.previous')}
              className='size-6 min-w-0 rounded-full p-0'
            />
          </motion.div>
        )}
      </AnimatePresence>
      <div className='relative flex min-w-0 flex-1'>
        <AnimatePresence initial={false}>
          {scrollShadows.left && (
            <motion.div
              key='shadow-left'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className='pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-gray-1 to-transparent'
              aria-hidden
            />
          )}
        </AnimatePresence>
        <div ref={scrollRef} className='no-scrollbar flex items-center gap-4 overflow-x-auto overflow-y-hidden'>
          <div ref={scrollContentRef} className='flex items-center gap-4'>
            <AnimatePresence initial={false} mode='popLayout'>
              {renderFilters?.map((filter) => {
                const options = filtersOptions?.find((f: any) => f.key === filter.key)?.options;
                return (
                  <motion.div
                    data-testid='datatable-filter'
                    key={filter.key}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className='flex shrink-0'
                  >
                    {filter.component({
                      filters: filtersDef,
                      onChangeFilter,
                      options: options,
                    })}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {scrollShadows.right && (
            <motion.div
              key='shadow-right'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className='pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-gray-1 to-transparent'
              aria-hidden
            />
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence initial={false}>
        {(scrollShadows.left || scrollShadows.right) && (
          <motion.div
            key='arrow-right'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className='shrink-0'
          >
            <Button
              variant='ghost'
              size='sm'
              color='default'
              icon={<Icon name='chevron-right' />}
              onClick={() => scrollBy(150)}
              disabled={!scrollShadows.right}
              aria-label={t('buttons.next')}
              className='size-6 min-w-0 rounded-full p-0'
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DataTableFilters;
